import { FC, useEffect, useMemo } from 'react';
import { Box, DropdownMenu, withStyles, ElinxType, TransactionType } from 'vopay';
import { useHistory, useParams } from 'react-router-dom';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { Trans, useTranslation } from 'react-i18next';
import { format, subYears } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { UtilButton, Title, Button, InfoBanner } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import { getFundingSettlementTimeLabel } from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';
import { useToast } from '@/providers/Toast.provider';
import useElinx from './useElinx';
import ElinxDetails from './ElinxDetails';
import styles from './ElinxDetails.module.scss';
import ApprovalPanel from '@/components/UI/ApprovalPanel/ApprovalPanel';

const cn = withStyles(styles);

const ElinxDetailsPage: FC = () => {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { ELinxId } = useParams<{ ELinxId: string }>();
  const { printFile, downloadPdfPage } = useFileDownload();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t: tFields } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const { details, fetchElinxDetails, cancelElinx } = useElinx();
  const { permissions } = usePermissions();
  const toast = useToast();

  // The eLinx placeholder's own transaction — fetched generically just to read ParentTransactionID,
  // mirroring ElinxConfirmationPage.tsx's Funded From/Funding Fee lookup.
  const { transactions: elinxPlaceholderTransactions, fetchTransactionsHistory: fetchElinxPlaceholderTransaction } =
    useFetchTransactionsHistory();
  // Separate instance so fetching the funding parent doesn't clobber elinxPlaceholderTransactions above.
  const {
    transactions: fundingParentTransactions,
    fetchTransactionsHistory: fetchFundingParentTransaction,
    fetching: fetchingFundingParentTransaction,
  } = useFetchTransactionsHistory();
  const { fundingOptions, fetchFundingOptions } = useFetchFundingOptions();

  useEffect(() => {
    if (!details || details.eLinxType !== 'ePay' || !details.TransactionID) return;

    fetchElinxPlaceholderTransaction({
      StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
      EndDateTime: formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd'),
      TransactionID: details.TransactionID,
    }).catch(() => {
      toast.neutral(t('Details.fetchFundingInfoError', { defaultValue: 'Error fetching funding information' }));
    });
  }, [details?.eLinxType, details?.TransactionID]);

  const elinxPlaceholderTransaction = useMemo(
    () => elinxPlaceholderTransactions.find((transaction) => transaction.TransactionID === details?.TransactionID),
    [elinxPlaceholderTransactions, details?.TransactionID],
  );

  useEffect(() => {
    const parentTransactionId = elinxPlaceholderTransaction?.ParentTransactionID;
    if (!parentTransactionId) return;

    Promise.all([
      fetchFundingParentTransaction({
        StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
        EndDateTime: formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd'),
        TransactionID: parentTransactionId,
      }),
      fetchFundingOptions({ Currency: details?.Currency || 'CAD' }),
    ]).catch(() => {
      toast.neutral(t('Details.fetchFundingInfoError', { defaultValue: 'Error fetching funding information' }));
    });
  }, [elinxPlaceholderTransaction?.ParentTransactionID, details?.Currency]);

  const fundingParentTransaction = useMemo(
    () =>
      fundingParentTransactions.find(
        (transaction) => transaction.TransactionID === elinxPlaceholderTransaction?.ParentTransactionID,
      ),
    [fundingParentTransactions, elinxPlaceholderTransaction?.ParentTransactionID],
  );

  const isFetchingFundingParent =
    !!elinxPlaceholderTransaction?.ParentTransactionID && !fundingParentTransaction && fetchingFundingParentTransaction;

  const fundingSettlementTime = useMemo(() => {
    if (!fundingParentTransaction) return '';
    const source = fundingParentTransaction.TransactionType === TransactionType.CREDIT_CARD ? 'credit' : 'bank';
    const option = fundingOptions.find((o) => o.Source === source);
    if (!option) return '';
    return getFundingSettlementTimeLabel(tFields, option.SettlementTime);
  }, [fundingParentTransaction, fundingOptions, tFields]);

  const getElinxDetails = async () => {
    try {
      await fetchElinxDetails({ ELinxRequestID: ELinxId });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchingError');
      alertDialog.error(t('ErrorMessages.fetchingError'), msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (!ELinxId) return;

    getElinxDetails();
  }, [ELinxId]);

  async function handleDialogue() {
    const result = await alertDialog.caution(
      t('Details.CancelElinx.title', {
        defaultValue: 'Are you sure you want to cancel eLinx Request?',
      }),
      t('Details.CancelElinx.description', {
        defaultValue: 'This action cannot be undone.',
      }),
      {
        abortLabel: tGlobal('Details.CancelElinx.cancelButton', {
          defaultValue: 'Go Back',
        }),
        confirmLabel: tGlobal('Details.CancelElinx.confirmButton', {
          defaultValue: 'Yes, Cancel eLinx Request',
        }),
      },
    );

    if (result !== 'confirm') return;

    try {
      await cancelElinx({ ELinxRequestID: ELinxId });

      const successResult = await alertDialog.success(
        t('Details.CancelElinx.successMessage', {
          defaultValue: 'The eLinx Request was successfully cancelled',
        }),
        '',
        {
          confirmLabel: t('Details.CancelElinx.successConfirmButton', {
            defaultValue: 'Back to Transaction',
          }),
        },
      );

      if (successResult === 'confirm') {
        history.goBack();
      }
    } catch (err) {
      const confirmRes = await alertDialog.caution(
        t('Details.CancelElinx.error', { defaultValue: 'Error! The eLinx Request was not cancelled.' }),
        err instanceof Error ? err.message : t('Details.unknownError', { defaultValue: 'Unknown error' }),
        {
          confirmLabel: tGlobal('Buttons.okay', { defaultValue: 'Okay' }),
        },
      );

      if (confirmRes === 'confirm') {
        history.goBack();
      }
    }
  }

  const elinxTypeMap: Record<ElinxType, string> = {
    ePay: t('Type.ePay', { defaultValue: 'ePay' }),
    eCollect: t('Type.eCollect', { defaultValue: 'eCollect' }),
    eConnect: t('Type.eConnect', { defaultValue: 'eConnect' }),
    eFlex: t('Type.eFlex', { defaultValue: 'eFlex' }),
  };

  if (!details) return null;

  return (
    <div className={cn('wrapper')}>
      <Box flx="col" gap="sm">
        <UtilButton iconLeft="chevron_left" size="large" onClick={() => history.goBack()}>
          {tGlobal('Buttons.back', { defaultValue: 'Back' })}
        </UtilButton>
        <Box jc="sb">
          <Title>{t('Details.title', { type: elinxTypeMap[details.eLinxType] })}</Title>
          <Box gapSize="8px">
            {(details.Status === 'pending' || details.Status === 'pending approval') &&
              permissions.ManageTransactions && (
                <Button onClick={handleDialogue}>
                  {t('Details.cancelButton', { defaultValue: 'Cancel eLinx Request' })}
                </Button>
              )}

            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button iconRight="keyboard_arrow_down">
                  {tGlobal('ExportDropdown.label', { defaultValue: 'Export' })}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => {
                    downloadPdfPage('eLinx-overview', t('title', { type: elinxTypeMap[details.eLinxType] }));
                  }}
                >
                  {tGlobal('ExportDropdown.Options.pdf', { defaultValue: 'PDF' })}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => {
                    printFile('eLinx-overview');
                  }}
                >
                  {tGlobal('ExportDropdown.Options.print', { defaultValue: 'Print' })}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        </Box>
      </Box>
      {details.Status === 'pending approval' && (
        <ApprovalPanel
          title={t('Approval.statusPending', { defaultValue: 'Pending Approval' })}
          description={t('Approval.description', {
            defaultValue: 'Approvers can find these transaction(s) in their Approval Inbox',
          })}
        />
      )}
      {details.Status === 'waiting' && fundingParentTransaction && (
        <InfoBanner colorScheme="neutral">
          <Trans
            t={t}
            i18nKey="Details.fundingWaiting"
            values={{ transactionId: fundingParentTransaction.TransactionID, settlementTime: fundingSettlementTime }}
            defaults="This request will be processed once funding transaction <bold>{{transactionId}}</bold> settles — typically {{settlementTime}}."
            components={{ bold: <span style={{ fontWeight: 600 }} /> }}
          />
        </InfoBanner>
      )}
      {/* The eLinx request itself completing (recipient submitted payment info) is independent of
          whether the resulting payment transaction then succeeds or fails — the stepper alone
          can't tell that story, so branch on the transaction's own status here. */}
      {details.Status === 'completed' && elinxPlaceholderTransaction && (
        <InfoBanner colorScheme={elinxPlaceholderTransaction.TransactionStatus === 'failed' ? 'error' : 'neutral'}>
          <Trans
            t={t}
            i18nKey={
              elinxPlaceholderTransaction.TransactionStatus === 'failed' ? 'Details.paymentFailed' : 'Details.paymentCompleted'
            }
            values={{ transactionId: elinxPlaceholderTransaction.TransactionID }}
            defaults={
              elinxPlaceholderTransaction.TransactionStatus === 'failed'
                ? 'This request completed, but payment <bold>{{transactionId}}</bold> failed and could not be processed.'
                : 'This request completed and payment <bold>{{transactionId}}</bold> was processed successfully.'
            }
            components={{ bold: <span style={{ fontWeight: 600 }} /> }}
          />
        </InfoBanner>
      )}
      <div className={cn('cardContainer')} id="eLinx-overview">
        <ElinxDetails
          details={details}
          fundingParentTransaction={fundingParentTransaction}
          isFetchingFundingParent={isFetchingFundingParent}
        />
      </div>
    </div>
  );
};

export default ElinxDetailsPage;
